import get from 'lodash/get';
import analytics from 'yoda-core-components/lib/helpers/Analytics/Analytics';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { logDAError } from 'yoda-interfaces/lib/Order/OrderApi';
import CustomErrorLogger from 'yoda-site-components/lib/helpers/CustomErrorLogger/CustomErrorLogger';
import { logwebvitals } from 'yoda-site-components/lib/middlewares/WebvitalsMiddleware';
import { LOG_CLIENT_ERROR } from 'yoda-site-components/lib/actionTypes/ClientLoggerActionTypes';
import * as CheckoutAnalyticsActionTypes from '../actionTypes/CheckoutAnalyticsActionTypes';
import CheckoutActionFilter from '../analytics/CheckoutActionFilter';
import {
    SINGLE_CLICK_CHECKOUT_ORDER_SUMMARY_CHEVRON,
    ANALYTICS_EVENTS,
    ANALYTICS_ACTIONS_LOGIN_SUCCESS,
    ANALYTICS_PAGE_NAME_CHECKOUT_ORDER_CONFIRMATION,
} from '../common/Constants';
import { createLogPayload } from '../helpers/Utils/ATCCommonUtils';
import productHelper from '../analytics/DigitalAnalyticsHelper';

const checkoutActionFilter = new CheckoutActionFilter();

/**
 * This function will used only for redux middleware only to track tagging based on action request
 */
const CheckoutAnalyticsMiddleware = (store) => (next) => (action) => {
    // eslint-disable-line
    if (!__SERVER__) {
        let digitalData = null;
        let analyticsEventName = null;
        let daHandler = null;
        const { context } = store.getState();
        const enableClickStreamLogs = get(context, 'featureFlags.enableClickStreamLogs', false);
        const enableOCPKibanaLog = get(context, 'featureFlags.enableOCPKibanaLog', true);
        /**
         * component can trigger an action only for tagging
         * action should contain analytics object as mandatory
         * @param analytics.type - {string}  - tagging key
         * @param analytics.JCPDL - {Object}  - required additional details for tagging event
         */
        try {
            switch (action.type) {
                case CheckoutAnalyticsActionTypes.ANALYTICS_PAGE_LOAD:
                    digitalData = checkoutActionFilter.updateCommonDigitalData(
                        digitalData,
                        context
                    );
                    digitalData = checkoutActionFilter.getPageDL(
                        store,
                        digitalData,
                        action.pageName
                    );
                    analyticsEventName = 'digitalDataReady';
                    // triggering webvitals on page load success
                    logwebvitals(store, digitalData);
                    break;

                case CheckoutAnalyticsActionTypes.ANALYTICS_CLICK_EVENT:
                    daHandler = checkoutActionFilter.clickHandler(store, action.payload);
                    digitalData = daHandler.digitalData;
                    analyticsEventName = daHandler.analyticsEventName;
                    break;
                /* istanbul ignore next */
                // when user toggle shipping type from HOME to STORE on checkout page, trigger this event
                case CheckoutAnalyticsActionTypes.TOGGLE_SHIPPING_TYPE_ANALYTICS_ACTION:
                    daHandler = {};
                    analyticsEventName = 'shipToStoreCheckout';
                    break;
                /* istanbul ignore next */
                case CheckoutAnalyticsActionTypes.ORDER_VARIANCE_SUBMIT_ORDER:
                    analyticsEventName = 'submitOrder';
                    digitalData = { ...action.payload };
                    digitalData.pageInfo = {
                        analyticsPageName: window?.digitalData?.page?.pageInfo?.pageName,
                        pageType: window?.digitalData?.page?.pageInfo?.pageType,
                    };
                    break;

                case ANALYTICS_ACTIONS_LOGIN_SUCCESS:
                    digitalData = action.payload;
                    if (action.payload.isPhone) {
                        analyticsEventName = 'loginSuccessTrigger';
                    } else {
                        analyticsEventName = ANALYTICS_EVENTS.LOGIN_SUCCESS;
                    }
                    break;
                case CheckoutAnalyticsActionTypes.ANALYTICS_GUEST_EMAIL_OPTIN:
                    digitalData = action.payload;
                    analyticsEventName = 'updateNotificationOptins';
                    break;
                case CheckoutAnalyticsActionTypes.ANALYTICS_CLICK_ORDER_SUMMARY_SCC:
                    daHandler = {};
                    analyticsEventName = SINGLE_CLICK_CHECKOUT_ORDER_SUMMARY_CHEVRON;
                    break;

                case CheckoutAnalyticsActionTypes.ANALYTICS_CLICK_GIFT_CARD_BANNER:
                    digitalData = {};
                    analyticsEventName = ANALYTICS_EVENTS.GIFT_CARD_BANNER;
                    break;
                case CheckoutAnalyticsActionTypes.PLCC_BANNER_UPDATE_DATA:
                    digitalData = checkoutActionFilter.pushPlccBannerData(action.bannerName);
                    analyticsEventName = 'pushPlccBanner';
                    break;

                case CheckoutAnalyticsActionTypes.PLCC_CLICK_EVENT_ACTION:
                    digitalData = action.payload.eventData;
                    analyticsEventName = action.payload.eventName;
                    break;
                case CheckoutAnalyticsActionTypes.GOOGLE_CAPTCHA_ANALYTICS_EVENT:
                    digitalData = action.payload?.data;
                    analyticsEventName = action.payload?.eventName;
                    break;

                default:
                    break;
            }
            if (analyticsEventName || digitalData) {
                const applyPayPreferences = get(context, 'preferences.applePay', {});
                /* istanbul ignore next */
                if (
                    enableOCPKibanaLog &&
                    action.type === CheckoutAnalyticsActionTypes.ANALYTICS_PAGE_LOAD &&
                    action.pageName === ANALYTICS_PAGE_NAME_CHECKOUT_ORDER_CONFIRMATION
                ) {
                    store.dispatch({
                        type: LOG_CLIENT_ERROR,
                        payload: {
                            errorType: `OCPage_analytticsLoad_before_middleware_trigger digitalReady-${digitalData.isReady}`,
                            errorDescription: `orderId - ${digitalData?.cart?.attributes?.shoppingBagNumber} orderNumber - ${digitalData?.transaction?.attributes?.transactionID}`,
                        },
                    });
                }
                /* istanbul ignore next */
                if (
                    analyticsEventName === 'digitalDataReady' &&
                    (window || {}).ApplePaySession &&
                    applyPayPreferences.enableCanMakePaymentsWithActiveCard
                ) {
                    const merchantId =
                        applyPayPreferences.merchantIdentifier ||
                        'merchant.com.applepaydev.jcpenney';
                    window.ApplePaySession.canMakePaymentsWithActiveCard(merchantId).then(
                        (response) => {
                            const storeCartDetails =
                                store.getState().cartDetails || store.getState().orderDetails;
                            const cartAttributesExist = get(digitalData, 'cart.attributes');
                            if (cartAttributesExist) {
                                digitalData.cart.attributes.deviceApplePayEligible = productHelper.findApplePayEligible(
                                    storeCartDetails.applePayEligible,
                                    response
                                );
                            }
                            analytics.track(analyticsEventName, digitalData, false);
                        }
                    );
                } else {
                    analytics.track(analyticsEventName, digitalData, false);
                }
            }
        } catch (error) /* istanbul ignore next */ {
            try {
                const pageNameAttribute = {
                    name: `DA_Exception_Caught:page_name | ${TokenProvider.get('ACCOUNT_ID')}`,
                    value: action.pageName,
                    type: 'string',
                };
                let payload = createLogPayload(pageNameAttribute);
                const errorLog = {
                    name: `DA_Exception_Caught:err| ${TokenProvider.get('ACCOUNT_ID')}`,
                    value: `${error.message}: ${error.stack}`,
                    type: 'string',
                };
                payload = createLogPayload(errorLog, payload);
                // call log api to log this error
                const params = { payload };
                enableClickStreamLogs
                    ? CustomErrorLogger.triggerCustomErrorLog(params)
                    : logDAError(params);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.log(err);
            }
        }
    }
    return next(action);
};
export default CheckoutAnalyticsMiddleware;
